import { toDateOnlyIso } from "./dataMappers";
import { isOpenRequest } from "./requestHelpers";

export const OVERLAPPING_LEAVE_MESSAGE =
  "يوجد طلب إجازة متداخل في نفس الفترة (قيد المراجعة أو معتمد). لا يمكن تقديم إجازة أخرى لنفس اليوم إلا إذا كانت نصّي شيفت مختلفين.";

export const OVERLAPPING_PERMIT_MESSAGE =
  "يوجد طلب إذن متداخل في نفس الفترة (قيد المراجعة أو معتمد)";

export const PERMIT_ON_LEAVE_MESSAGE =
  "يوجد طلب إجازة معتمد أو قيد المراجعة في نفس اليوم. لا يمكن تقديم إذن على يوم إجازة كامل.";

export const LEAVE_ON_PERMIT_MESSAGE =
  "يوجد طلب إذن معتمد أو قيد المراجعة في نفس الفترة. لا يمكن تقديم إجازة كاملة على يوم فيه إذن.";

function isLeaveRequest(request) {
  if (!request) return false;
  if (request._source === "permit") return false;
  return (
    request._source === "leave" ||
    (request.category === "leave" && request._source !== "permit") ||
    String(request.type || "").includes("إجازة")
  );
}

function isPermitRequest(request) {
  if (!request) return false;
  return (
    request._source === "permit" ||
    request.category === "permit" ||
    request.type === "إذن" ||
    request.type === "إذن تعويضي"
  );
}

function isBlockingLeaveStatus(status) {
  return isOpenRequest({ status }) || status === "approved";
}

function isHalfDayLeave(request) {
  const period = request?.halfDayPeriod;
  if (period === "FIRST_HALF" || period === "SECOND_HALF") return true;
  return Number(request?.days) === 0.5;
}

function areComplementaryLeaveHalves(a, b) {
  return (
    (a === "FIRST_HALF" && b === "SECOND_HALF") ||
    (a === "SECOND_HALF" && b === "FIRST_HALF")
  );
}

export function leaveDateRangesOverlap(startA, endA, startB, endB) {
  const aStart = toDateOnlyIso(startA);
  const aEnd = toDateOnlyIso(endA) || aStart;
  const bStart = toDateOnlyIso(startB);
  const bEnd = toDateOnlyIso(endB) || bStart;
  if (!aStart || !bStart) return false;
  return aStart <= bEnd && bStart <= aEnd;
}

export function leaveRequestsConflict(existing, incoming) {
  if (
    !leaveDateRangesOverlap(
      existing?.startDate,
      existing?.endDate,
      incoming?.startDate,
      incoming?.endDate,
    )
  ) {
    return false;
  }
  if (!isHalfDayLeave(existing) || !isHalfDayLeave(incoming)) {
    return true;
  }
  return !areComplementaryLeaveHalves(
    existing.halfDayPeriod,
    incoming.halfDayPeriod,
  );
}

export function findOverlappingLeaveRequest(requests, incoming, isOwnRequest) {
  return (requests || []).find(
    (request) =>
      (!isOwnRequest || isOwnRequest(request)) &&
      isLeaveRequest(request) &&
      isBlockingLeaveStatus(request.status) &&
      leaveRequestsConflict(request, incoming),
  );
}

function timeToMinutes(value) {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

export function permitWindowsOverlap(aStart, aEnd, bStart, bEnd) {
  const toAbs = (start, end) => {
    const s = timeToMinutes(start);
    let e = timeToMinutes(end);
    if (e <= s) e += 24 * 60;
    return [s, e];
  };
  const [a0, a1] = toAbs(aStart, aEnd);
  const [b0, b1] = toAbs(bStart, bEnd);
  return a0 < b1 && b0 < a1;
}

function permitDateIso(request) {
  return toDateOnlyIso(request?.permitDate || request?.startDate);
}

export function permitRequestsConflict(existing, incoming) {
  const existingDate = permitDateIso(existing);
  const incomingDate = toDateOnlyIso(incoming?.permitDate || incoming?.startDate);
  if (!existingDate || existingDate !== incomingDate) return false;
  return permitWindowsOverlap(
    existing.permitStartTime || existing.startTime,
    existing.permitEndTime || existing.endTime,
    incoming.permitStartTime || incoming.startTime,
    incoming.permitEndTime || incoming.endTime,
  );
}

export function findOverlappingPermitRequest(requests, incoming, isOwnRequest) {
  return (requests || []).find(
    (request) =>
      (!isOwnRequest || isOwnRequest(request)) &&
      isPermitRequest(request) &&
      isBlockingLeaveStatus(request.status) &&
      permitRequestsConflict(request, incoming),
  );
}

export function leaveCoversPermitDate(leave, permitDate) {
  if (!leaveDateRangesOverlap(leave?.startDate, leave?.endDate, permitDate, permitDate)) {
    return false;
  }
  return !isHalfDayLeave(leave);
}

export function findLeaveCoveringPermitDate(requests, permitDate, isOwnRequest) {
  return (requests || []).find(
    (request) =>
      (!isOwnRequest || isOwnRequest(request)) &&
      isLeaveRequest(request) &&
      isBlockingLeaveStatus(request.status) &&
      leaveCoversPermitDate(request, permitDate),
  );
}

export function findPermitCoveringLeaveRange(requests, leaveRange, isOwnRequest) {
  if (isHalfDayLeave(leaveRange)) return undefined;
  return (requests || []).find((request) => {
    if (isOwnRequest && !isOwnRequest(request)) return false;
    if (!isPermitRequest(request) || !isBlockingLeaveStatus(request.status)) {
      return false;
    }
    const date = permitDateIso(request);
    return leaveDateRangesOverlap(
      leaveRange?.startDate,
      leaveRange?.endDate,
      date,
      date,
    );
  });
}
